import {
    type Dispatch,
    type RefObject,
    type SetStateAction,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
} from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useDebounceCallback } from 'usehooks-ts';
import type { MapRef } from 'react-map-gl/maplibre';

import { useClient } from '@/clients/useClient';
import type { MapViewMode } from '@/components/map/utils/basemaps';
import { flyToClientNetwork } from '@/components/map/utils/semanticZoom';
import type { MapFilterState } from '@/components/map/utils/mapFilters';
import type { EntityToggleState, FeatureOverlayState, PoiTypeState } from '@/components/map/utils/mapLayers';
import type { MapViewport } from '@/components/map/utils/mapPreferences';
import {
    buildMapSearchParams,
    canonicaliseSearch,
    deserializeFiltersFromUrl,
    deserializeLayersFromUrl,
    type MapSearchParams,
} from '@/components/map/utils/mapUrlState';
import { DEFAULT_MAP_FILTER_STATE } from '@/components/map/utils/mapFilters';
import { DEFAULT_ENTITY_MAP_FILTER_STATE, type EntityMapFilterState } from '@/components/map/utils/entityMapFilters';
import {
    deserializeEntityFiltersFromUrl,
    deserializeEntityLayersFromUrl,
    serializeEntityFiltersToUrl,
    serializeEntityLayersToUrl,
} from '@/components/map/utils/entityMapUrlState';

export interface MapUrlSyncOptions {
    mapRef: RefObject<MapRef | null>;
    isMapLoaded: boolean;
    /**
     * Whether an initial viewport was restored at mount — from a shared link (lat/lng/z search
     * params) or persisted localStorage. When false (a true cold start), the focal-point effect
     * flies to the selected client's network on first load instead of leaving the camera on the
     * default fallback view; when true, the restored/shared viewport is respected and no fly occurs.
     */
    hasRestoredViewport: boolean;
    /**
     * Whether this map belongs to the selected client. Default true: the URL names the client,
     * a client switch rewrites the URL and flies the camera to the client's focal point, and a
     * `client` search param is applied as a switch on load. Network Explorer passes false — it
     * shows every client, so its URL never names one and the selection underneath it is none
     * of its business. That last point matters for the "Open in Find & Fix" hand-off's in-tab
     * fallback, which switches client while the explorer's map is still mounted: a URL write
     * reacting to that switch would be coalesced with the pending hand-off navigation and
     * overwrite the record it carries.
     */
    followsClient?: boolean;
    selectedBasemapId: string;
    viewMode: MapViewMode;
    featureOverlays: FeatureOverlayState;
    poiTypes: PoiTypeState;
    validBasemapIds: string[];
    setSelectedBasemapId: Dispatch<SetStateAction<string>>;
    setViewMode: Dispatch<SetStateAction<MapViewMode>>;
    setFeatureOverlays: Dispatch<SetStateAction<FeatureOverlayState>>;
    setPoiTypes: Dispatch<SetStateAction<PoiTypeState>>;
    filterIsActive?: boolean;
    filterCommitted?: MapFilterState;
    setFilterFromUrl?: (state: { isActive: boolean; committed: MapFilterState } | null) => void;
    /**
     * Split-model URL encoding: when true the `layers` param carries entity-toggle tokens
     * (serialised with the entity codec, honouring the legacy-token fallback on read) and
     * the `filters` param uses the `v2:` entity payload. The legacy codecs still drive
     * both params when false (SAVI, or an API without the entity contract).
     */
    entityMode?: boolean;
    entityToggles?: EntityToggleState;
    setEntityToggles?: Dispatch<SetStateAction<EntityToggleState>>;
    entityFilterIsActive?: boolean;
    entityFilterCommitted?: EntityMapFilterState;
    setEntityFilterFromUrl?: (state: { isActive: boolean; committed: EntityMapFilterState } | null) => void;
    /**
     * Which search param carries the entity filter state. Find & Fix uses `filters`; the
     * Network Explorer uses `explorerFilters`, its own param, so neither mode's URL can
     * ever be read as the other's filter state. The param not in use is never written.
     */
    filtersParam?: 'filters' | 'explorerFilters';
}

/**
 * Manages bidirectional URL synchronisation for map state.
 *
 * - State → URL: writes basemap, view mode, layers, client, and viewport to search
 *   params using replace navigation (no history pollution). Suppressed until the map
 *   has settled after first load to avoid writing noisy defaults on fresh pages.
 *
 * - URL → state (back/forward): detects external URL changes by comparing the
 *   incoming search against a canonicalised snapshot of the last params we wrote.
 *   A match means it is our own echo; a mismatch means a genuine back/forward
 *   navigation — apply to React state and jump the camera.
 *
 * - Client sync: one-time effect after the client list loads, switches to the
 *   client specified in the URL if it differs from the current selection.
 *
 * Returns `syncViewportToUrl` — call this from the map's onMoveEnd handler.
 */
export function useMapUrlSync({
    mapRef,
    isMapLoaded,
    hasRestoredViewport,
    followsClient = true,
    selectedBasemapId,
    viewMode,
    featureOverlays,
    poiTypes,
    validBasemapIds,
    setSelectedBasemapId,
    setViewMode,
    setFeatureOverlays,
    setPoiTypes,
    filterIsActive,
    filterCommitted,
    setFilterFromUrl,
    entityMode = false,
    entityToggles,
    setEntityToggles,
    entityFilterIsActive,
    entityFilterCommitted,
    setEntityFilterFromUrl,
    filtersParam = 'filters',
}: MapUrlSyncOptions) {
    const navigate = useNavigate();
    const search = useSearch({ strict: false });
    const { selectedClient, clients, isLoading: isClientLoading, switchClient } = useClient();
    // The client this map's URL names and reacts to — none for a map that shows every client.
    const urlClientId = followsClient ? selectedClient?.id : undefined;

    // Canonicalised form of the last search params we wrote via navigate().
    // Initialised from the URL state present at mount so the URL→state effect
    // treats the initial params as "already applied" rather than a back/forward.
    const lastWrittenCanonRef = useRef(canonicaliseSearch(search));

    // True after the first onMoveEnd — or immediately if the URL already contained
    // lat/lng/z at mount (shared link). Controls whether viewport is included in
    // state→URL writes; without it the URL would drop coordinates on client switch.
    const hasSettledRef = useRef(search.lat !== undefined && search.lng !== undefined && search.z !== undefined);

    // Tracks the latest viewport for the state→URL effect. Pre-populated from URL
    // params when present so a client switch on a shared-link page preserves location.
    const currentViewportRef = useRef<MapViewport | undefined>(
        search.lat !== undefined && search.lng !== undefined && search.z !== undefined
            ? { latitude: search.lat, longitude: search.lng, zoom: search.z, pitch: 0, bearing: 0 }
            : undefined,
    );

    // Refs to latest state so the stable debounced viewport callback always
    // reads the most recent values without depending on them directly.
    const latestStateRef = useRef({
        selectedBasemapId,
        viewMode,
        featureOverlays,
        poiTypes,
        filterIsActive,
        filterCommitted,
        entityMode,
        entityToggles,
        entityFilterIsActive,
        entityFilterCommitted,
        filtersParam,
    });
    const latestClientIdRef = useRef(urlClientId);
    const navigateRef = useRef(navigate);

    // Keep refs in sync with the latest render values after each commit.
    useLayoutEffect(() => {
        latestStateRef.current = {
            selectedBasemapId,
            viewMode,
            featureOverlays,
            poiTypes,
            filterIsActive,
            filterCommitted,
            entityMode,
            entityToggles,
            entityFilterIsActive,
            entityFilterCommitted,
            filtersParam,
        };
        latestClientIdRef.current = urlClientId;
        navigateRef.current = navigate;
    });

    // Guards the one-time client URL sync.
    const hasInitialisedClientRef = useRef(false);

    // Tracks the client ID at the time the map first loaded — used to distinguish
    // initial load (where a shared-link viewport takes priority) from a mid-session switch.
    const initialClientIdRef = useRef<string | null>(null);

    // ── State → URL ──────────────────────────────────────────────────────────

    // Builds the search params from the latest state snapshot, swapping the layers/filters
    // encodings to the entity codecs in entity mode (the other params are shared). Reads
    // only refs, so it is stable and both write paths can share it.
    const buildParams = useCallback((viewport: MapViewport | undefined): MapSearchParams => {
        const latest = latestStateRef.current;
        const params = buildMapSearchParams({
            ...latest,
            viewport,
            clientId: latestClientIdRef.current,
        });
        if (latest.entityMode) {
            if (latest.entityToggles) {
                params.layers = serializeEntityLayersToUrl(latest.featureOverlays, latest.entityToggles);
            }
            // The entity filter state goes under this mode's own param; the legacy `filters`
            // value the shared builder wrote is dropped when it is not that param.
            params.filters = undefined;
            params[latest.filtersParam] =
                latest.entityFilterIsActive !== undefined
                    ? serializeEntityFiltersToUrl(
                          latest.entityFilterIsActive,
                          latest.entityFilterCommitted ?? DEFAULT_ENTITY_MAP_FILTER_STATE,
                      )
                    : undefined;
        }
        return params;
    }, []);

    useEffect(() => {
        if (!isMapLoaded) return;

        // Only include viewport after the map has settled (first onMoveEnd or shared link).
        // This avoids writing bare default params to a clean URL on fresh page loads.
        const params = buildParams(hasSettledRef.current ? currentViewportRef.current : undefined);

        const canon = canonicaliseSearch(params);
        if (canon === lastWrittenCanonRef.current) return;
        lastWrittenCanonRef.current = canon;

        // useNavigate() without `from` resolves search to `never` in TanStack Router's
        // strict generic. Both routes use identical MapSearchParams so this is safe.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
        void navigateRef.current({ search: params as any, replace: true });
        // urlClientId in deps ensures a client switch triggers this effect even before the user
        // has panned the map (previously blocked by hasSettledRef gate) — for a map that follows
        // the client; the explorer's URL must not react to the selection underneath it.
        // filterIsActive and filterCommitted both trigger URL writes: filterIsActive alone misses
        // re-applies where isActive stays true but committed values change (e.g. user applies twice).
        // filterCommitted is a React state value so its reference only changes on Apply/Reset/setFromUrl.
        // canonicaliseSearch prevents spurious navigate calls when the serialised form is unchanged.
    }, [
        selectedBasemapId,
        viewMode,
        featureOverlays,
        poiTypes,
        filterIsActive,
        filterCommitted,
        entityToggles,
        entityFilterIsActive,
        entityFilterCommitted,
        isMapLoaded,
        urlClientId,
        buildParams,
    ]);

    // ── Viewport → URL (debounced, called from onMoveEnd) ────────────────────

    const rawSyncViewport = useCallback(
        (viewport: MapViewport) => {
            currentViewportRef.current = viewport;
            hasSettledRef.current = true;

            const params = buildParams(viewport);

            const canon = canonicaliseSearch(params);
            if (canon === lastWrittenCanonRef.current) return;
            lastWrittenCanonRef.current = canon;

            // useNavigate() without `from` resolves search to `never` in TanStack Router's
            // strict generic. Both routes use identical MapSearchParams so this is safe.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
            void navigateRef.current({ search: params as any, replace: true });
        },
        [buildParams],
    );

    const syncViewportToUrl = useDebounceCallback(rawSyncViewport, 300);

    // ── URL → state (back/forward detection) ─────────────────────────────────

    useEffect(() => {
        if (!isMapLoaded) return;

        const incomingCanon = canonicaliseSearch(search);
        if (incomingCanon === lastWrittenCanonRef.current) return;

        // External URL change (browser back/forward) — apply to state.
        if (search.style && validBasemapIds.includes(search.style)) {
            setSelectedBasemapId(search.style);
        }
        if (search.view) {
            setViewMode(search.view);
        }
        if (search.layers !== undefined) {
            if (entityMode) {
                const { featureOverlays: newOverlays, entityToggles: newToggles } = deserializeEntityLayersFromUrl(
                    search.layers,
                );
                setFeatureOverlays(newOverlays);
                setEntityToggles?.(newToggles);
            } else {
                const { featureOverlays: newOverlays, poiTypes: newPoiTypes } = deserializeLayersFromUrl(search.layers);
                setFeatureOverlays(newOverlays);
                setPoiTypes(newPoiTypes);
            }
        }
        if (search.lat !== undefined && search.lng !== undefined && search.z !== undefined) {
            mapRef.current?.jumpTo({ center: [search.lng, search.lat], zoom: search.z });
        }
        // filters param present: apply URL filter state (valid or explicit inactive).
        // filters param absent: do not override current filter state on external navigation.
        // An unparseable value (including a cross-version prefix) forces inactive rather than
        // falling back to the recipient's own persisted filters. In entity mode the param read
        // is this mode's own (`filters` or `explorerFilters`); the other mode's is ignored.
        if (entityMode) {
            const entityFiltersParam = search[filtersParam];
            if (entityFiltersParam !== undefined) {
                setEntityFilterFromUrl?.(
                    deserializeEntityFiltersFromUrl(entityFiltersParam) ?? {
                        isActive: false,
                        committed: DEFAULT_ENTITY_MAP_FILTER_STATE,
                    },
                );
            }
        } else if (search.filters !== undefined) {
            setFilterFromUrl?.(
                deserializeFiltersFromUrl(search.filters) ?? {
                    isActive: false,
                    committed: { ...DEFAULT_MAP_FILTER_STATE },
                },
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps, react-x/exhaustive-deps
    }, [
        search.lat,
        search.lng,
        search.z,
        search.style,
        search.view,
        search.layers,
        search.filters,
        search.explorerFilters,
        isMapLoaded,
    ]);

    // ── Client URL sync (one-time after client list loads) ───────────────────

    useEffect(() => {
        if (!followsClient) return;
        if (isClientLoading || clients.length === 0) return;
        if (hasInitialisedClientRef.current) return;
        hasInitialisedClientRef.current = true;

        if (search.client && search.client !== selectedClient?.id) {
            switchClient(search.client);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps, react-x/exhaustive-deps
    }, [isClientLoading, clients.length]);

    // ── Focal point fly-to on client load / switch ────────────────────────────

    useEffect(() => {
        if (!followsClient) return;
        if (!isMapLoaded || !selectedClient) return;

        const isInitialLoad = initialClientIdRef.current === null;
        initialClientIdRef.current = selectedClient.id;

        // Fly to the client's network when the user actively switches client. On the very first
        // load, only fly when there is no viewport to respect — a cold start with no shared-link
        // params and no persisted localStorage viewport. That lands a first-time user on their
        // network without overriding a restored or shared viewport.
        if (isInitialLoad && hasRestoredViewport) return;

        flyToClientNetwork(mapRef.current, selectedClient.focalPoint);
        // eslint-disable-next-line react-hooks/exhaustive-deps, react-x/exhaustive-deps
    }, [selectedClient?.id, isMapLoaded]);

    return { syncViewportToUrl };
}
